/**
 * envConfig.ts
 *
 * Single source of truth for all environment variables.
 *
 * Priority (highest → lowest):
 *   1. window.__ENV__  — injected at runtime via OpenShift/K8s ConfigMap (env-config.js)
 *   2. import.meta.env — baked in at Vite build time (used in local dev / CI builds)
 *   3. fallback value  — optional hard-coded default per key
 *
 * This means:
 *  - In OpenShift: ConfigMap controls all URLs → no rebuild needed per environment.
 *  - Locally:      .env / .env.development files are used as normal.
 */

type WindowWithEnv = Window & {
    __ENV__?: Record<string, string | undefined>;
};

/**
 * Read a value from window.__ENV__ (runtime ConfigMap) first,
 * then fall back to import.meta.env (build-time), then to an optional default.
 */
const getEnv = (key: string, defaultValue?: string): string => {
    const runtimeEnv = (window as WindowWithEnv).__ENV__;
    const runtimeValue = runtimeEnv?.[key];
    if (runtimeValue !== undefined && runtimeValue !== '') {
        return runtimeValue;
    }

    const buildTimeValue = (import.meta.env as Record<string, string | undefined>)[key];
    if (buildTimeValue !== undefined && buildTimeValue !== '') {
        return buildTimeValue;
    }

    if (defaultValue !== undefined) {
        return defaultValue;
    }

    return '';
};

// ── Service base URLs ──────────────────────────────────────────────────────────
const FRONTEND_BASE_URL = getEnv('VITE_FRONTEND_BASE_URL', window.location.origin);
export const SHARED_SERVICE_BASE_URL = FRONTEND_BASE_URL;
export const AUTH_SERVICE_BASE_URL = FRONTEND_BASE_URL;
export const CUSTOMER_SERVICE_BASE_URL = FRONTEND_BASE_URL;
export const SHIPMENT_SERVICE_BASE_URL = FRONTEND_BASE_URL;
export const QUOTATION_SERVICE_BASE_URL = FRONTEND_BASE_URL;
export const CONFIGURATION_SERVICE_BASE_URL = FRONTEND_BASE_URL;
export const CONSENT_SERVICE_BASE_URL = FRONTEND_BASE_URL;
export const PAYMENT_SERVICE_BASE_URL = FRONTEND_BASE_URL;
export const INVENTORY_SERVICE_BASE_URL = FRONTEND_BASE_URL;
export const NOTIFICATION_SERVICE_BASE_URL = FRONTEND_BASE_URL;

// Legacy/fallback base URL keys
export const API_BASE_URL = FRONTEND_BASE_URL;
export const SP_PROFILE_BASE_URL = FRONTEND_BASE_URL;
export const SP_MGMT_BASE_URL = FRONTEND_BASE_URL;

// ── Context paths ──────────────────────────────────────────────────────────────
export const SHARED_CONTEXT = getEnv('VITE_SHARED_CONTEXT', '/shared-service');
export const AUTH_CONTEXT = getEnv('VITE_AUTH_CONTEXT', '/auth-service');
export const CUSTOMER_CONTEXT = getEnv('VITE_CUSTOMER_CONTEXT', '/customer-service');
export const SHIPMENT_CONTEXT = getEnv('VITE_SHIPMENT_CONTEXT', '/shipment-service');
export const QUOTATION_CONTEXT = getEnv('VITE_QUOTATION_CONTEXT', '/quotation-service');
export const CONFIGURATION_CONTEXT = getEnv('VITE_CONFIGURATION_CONTEXT', '/configuration-service');
export const CONSENT_CONTEXT = getEnv('VITE_CONSENT_CONTEXT', '/consent-service');
export const PAYMENT_CONTEXT = getEnv('VITE_PAYMENT_CONTEXT', '/payment-service');
export const INVENTORY_CONTEXT = getEnv('VITE_INVENTORY_CONTEXT', '/shipment-inventory-service');
export const NOTIFICATION_CONTEXT = getEnv('VITE_NOTIFICATION_CONTEXT', '/notification-service');

// ── SSO ────────────────────────────────────────────────────────────────────────
export const SSO_REDIRECT_URI = getEnv('VITE_SSO_REDIRECT_URI');
export const AZURE_AD_AUTHORITY_BASE_URL = getEnv(
    'VITE_AZURE_AD_AUTHORITY_BASE_URL',
    'https://login.microsoftonline.com',
);

// ── External services ──────────────────────────────────────────────────────────
export const DHL_AVW_BASE_URL = getEnv(
    'VITE_DHL_AVW_BASE_URL',
    'https://aol.uat.addressecosystem.com',
);
export const GOOGLE_MAPS_BASE_URL = getEnv(
    'VITE_GOOGLE_MAPS_BASE_URL',
    'https://maps.googleapis.com',
);
export const GOOGLE_MAPS_API_KEY = getEnv('VITE_GOOGLE_MAPS_API_KEY');
export const DHL_ONI_API_KEY = getEnv('VITE_DHL_ONI_API_KEY');

// ── Misc ───────────────────────────────────────────────────────────────────────
export const RUNTIME_MODE = getEnv('MODE', import.meta.env.MODE);
